// @flow
import React, { type Node } from 'react';
import Helmet from 'react-helmet-async';
import { get, sortBy } from 'lodash';
import { compose, pure } from 'recompose';
import { hot } from 'react-hot-loader';
import { connect } from 'react-redux';
import { Grid, Row, Col, Alert } from 'angieslist-react-components';
import { analytics, urlCatalog } from 'angieslist-app-platform';
import Loader from 'angieslist-app-platform/lib/browser/Loader';
import CategoryGroupMetrics from './components/CategoryGroupMetrics';
import PerformanceViewHeader from './components/PerformanceViewHeader';
import performanceActions from '../common/performanceActions';
import type { GroupPerformanceReducerState } from '../common/performanceReducer';
import './PerformanceView.scss';
import { defaultPageProps } from '../../../../src/app/browser/analyticsDefaultProperties';

export type Props = {
  performance: GroupPerformanceReducerState,
  getContactMetricsCategoryGroupsByServiceProvider:
    typeof performanceActions.getContactMetricsCategoryGroupsByServiceProvider,
  params: {
    serviceProviderId: string
  },
};

export class PerformanceViewComponent extends React.Component<Props> {
  componentDidMount() {
    const { params } = this.props;
    const serviceProviderId = get(params, 'serviceProviderId');
    const endTime = new Date();
    const startTime = new Date(new Date().setDate(endTime.getDate() - 90));
    this.props
      .getContactMetricsCategoryGroupsByServiceProvider({ serviceProviderId, startTime, endTime });
  }

  props: Props;

  render(): Node {
    const {
      performance: {
        contactMetricsByCategory: {
          response: contactMetricsByCategoryResponse,
          isLoading: contactMetricsByCategoryLoading,
          error: contactMetricsByCategoryError,
        },
      },
      params,
    } = this.props;

    const serviceProviderId = get(params, 'serviceProviderId');
    const categoryData = get(contactMetricsByCategoryResponse, 'categoryGroupMetrics');

    // Prioritize advertising categories at the top
    const categoryDataSorted =
      sortBy(categoryData, c => c.categoryGroup.hasAdvertisingIn)
        .reverse();

    return (
      <div id={`performance-view--${serviceProviderId}`} className="performance-view-wrapper">
        <Grid>
          <Helmet title="Performance" />
          <Row>
            <Col xs={9}>
              <h1>Performance</h1>
            </Col>
            <Col xs={3} className="last-90-days-header">
              <em>Last 90 days</em>
            </Col>
          </Row>
          <Loader
            loading={contactMetricsByCategoryLoading}
            element={
              <Row className="category-row text-center" style={{ marginTop: '1em' }}>
                <i className="icon icon-load icon-spin icon-3x text-primary" />
              </Row>}
          >
            <Row>
              <Col className="performance-page-header" xs={12}>
                <If condition={contactMetricsByCategoryError}>
                  <Alert bsStyle="danger">There was a problem retrieving your performance metrics.
                  Please contact us at&nbsp;
                    <a
                      id="service-areas--add-service-area--store-error-link"
                      href={urlCatalog.global.businessCenterPhone()}
                      style={{ whiteSpace: 'nowrap' }}
                    >{urlCatalog.global.businessCenterPhoneText()}
                    </a> for assistance.
                  </Alert>
                </If>
                {
                  // isAdvertiser = true due to non advertisers not having access to this page
                }
                <PerformanceViewHeader
                  serviceProviderId={serviceProviderId}
                  contactMetrics={contactMetricsByCategoryResponse}
                  isAdvertiser
                />
              </Col>
            </Row>
            <Row>
              <Col xs={12}>
                <h3 style={{ marginBottom: '0.8em' }}>Breakdown by Category Groups</h3>
              </Col>
            </Row>
            <Row>
              <Col xs={12} md={4}>
                <div className="calls-explanation">
                  <strong>How we know about phone calls</strong>
                  <p>
                    Customers are required to click to reveal your phone number, and this
                    lets us know what categories they are likely contacting you about. This
                    data helps us estimate what categories your calls were about.
                  </p>
                </div>
              </Col>
              <Col xs={12} md={8}>
                <If condition={categoryDataSorted}>
                  {
                  categoryDataSorted.map(singleCategoryMetric => (
                    <CategoryGroupMetrics
                      key={singleCategoryMetric.categoryGroup.name}
                      singleCategoryMetric={singleCategoryMetric}
                      serviceProviderId={serviceProviderId}
                      isAdvertiser
                    />
                        ))
                      }
                </If>
              </Col>
            </Row>
          </Loader>
        </Grid>
      </div>
    );
  }
}

const performanceAnalytics = {
  page: {
    name: 'SP : Performance',
    properties: defaultPageProps,
  },
};

export default (hot(module)(compose(
  analytics({
    ruleset: performanceAnalytics,
  }),
  connect(
    state => ({
      user: state.user,
      performance: state.performance,
    }),
    {
      ...performanceActions,
    },
  ),
  pure,
)(PerformanceViewComponent)): any);



// WEBPACK FOOTER //
// ./src/modules/home/browser/PerformanceView.js